import type { PersonId } from '@shared/brand';
import type { BalanceRef } from '@application/ports';
import { availableDays, type Balance } from '@domain/entities/Balance';
import {
  EXPIRY_NOTICE_DAYS,
  lastValidDay,
  livePeriodsOn,
  periodFor,
  periodYearOf,
} from '@domain/policies/VacationPeriodPolicy';
import { CalendarDate } from '@domain/value-objects/CalendarDate';

/**
 * Cómo la pantalla sabe qué saldo pedir.
 *
 * El saldo se guarda por periodo, y el periodo va de aniversario a aniversario:
 * el documento de quien entró un 16 de septiembre está etiquetado con el año de
 * su último aniversario, que en agosto todavía es el anterior. Pedir «el saldo
 * de 2026» —que es lo que hacía cada pantalla— acertaba sólo con quien entró en
 * enero, y para el resto pedía un documento que no existe o el del periodo
 * equivocado.
 *
 * Vive aquí, en una función, para que las pantallas que leen saldos hagan
 * la misma cuenta que hace el servidor al escribirlos. Es la misma política del
 * dominio, no una copia.
 */

/** El periodo vivo de una persona. Sin fecha de ingreso, el año en curso. */
export const periodYearFor = (hireDate: CalendarDate | null, today: CalendarDate): number =>
  hireDate === null ? today.year : periodYearOf(hireDate, today);

// ---------------------------------------------------------------------------
// Periodos encimados
//
// Con «Vencimiento del saldo» en más de 12 meses, una persona tiene a la vez el
// periodo que abrió en su último aniversario y lo que le quede del anterior, que
// sigue valiendo hasta N meses después del suyo. Las pantallas los enseñan por
// separado, cada uno con su fecha, y suman cuando lo que cabe es una cifra.
// ---------------------------------------------------------------------------

/** Un periodo vigente hoy, sin saldo todavía. */
export interface LivePeriodInfo {
  readonly year: number;
  /** El último día en que sus días se pueden gozar. */
  readonly lastValidDay: CalendarDate;
}

/**
 * Los periodos vigentes hoy, del que vence antes al que vence después.
 *
 * Nunca vacío: sin fecha de ingreso es el año natural, y en el primer año —que
 * no vence, madura— es el periodo que corre, igual que hacía `periodYearFor`.
 */
export const livePeriodsFor = (
  hireDate: CalendarDate | null,
  today: CalendarDate,
  expiryMonths: number,
): readonly LivePeriodInfo[] => {
  if (hireDate === null) {
    return [
      {
        year: today.year,
        lastValidDay: CalendarDate.fromISOOrThrow(`${String(today.year)}-12-31`),
      },
    ];
  }
  const vivos = livePeriodsOn(hireDate, today, expiryMonths);
  if (vivos.length === 0) {
    const corriente = periodFor(hireDate, today);
    return [{ year: corriente.year, lastValidDay: corriente.lastDay }];
  }
  return vivos.map((period) => ({
    year: period.year,
    lastValidDay: lastValidDay(period, expiryMonths),
  }));
};

/** Un periodo vigente con su saldo. */
export interface LiveBalance extends LivePeriodInfo {
  readonly balance: Balance;
  readonly available: number;
  /** Días naturales hasta `lastValidDay`. */
  readonly daysLeft: number;
}

/**
 * Empareja los periodos vigentes con los saldos leídos.
 *
 * Un periodo sin documento se omite, como antes se omitía la tarjeta. Uno viejo
 * que ya no tiene días también: no hay nada que gozar ni que perder, y dos
 * tarjetas donde una dice cero confunden. El más nuevo se queda siempre, porque
 * es el saldo de la persona aunque esté en cero.
 */
export const pairLiveBalances = (
  periods: readonly LivePeriodInfo[],
  balances: readonly Balance[],
  personId: PersonId,
  today: CalendarDate,
): readonly LiveBalance[] => {
  const newest = periods[periods.length - 1]?.year;
  return periods.flatMap((period) => {
    const balance = balances.find((b) => b.personId === personId && b.year === period.year);
    if (balance === undefined) return [];
    const available = availableDays(balance);
    if (available === 0 && period.year !== newest) return [];
    return [
      {
        ...period,
        balance,
        available,
        daysLeft: period.lastValidDay.epochDay - today.epochDay,
      },
    ];
  });
};

/** Lo que se puede pedir hoy, entre todos los periodos vigentes. */
export const totalAvailable = (lives: readonly LiveBalance[]): number =>
  lives.reduce((sum, live) => sum + live.available, 0);

/** El periodo más nuevo: el que da el derecho de este año y el rótulo «periodo N». */
export const newestLive = (lives: readonly LiveBalance[]): LiveBalance | null =>
  lives[lives.length - 1] ?? null;

/**
 * Los días que se pierden pronto, si toca avisar.
 *
 * El periodo vigente que vence antes y todavía tiene días, sólo dentro de la
 * ventana de aviso: la misma de `remindExpiringDays`, para que la barra lateral,
 * Inicio, Reportes y el correo hablen del mismo momento.
 */
export const expiringLive = (
  lives: readonly LiveBalance[],
  hireDate: CalendarDate | null,
  today: CalendarDate,
): LiveBalance | null => {
  /* El primer año no vence, madura; y en él no hay otro periodo que pueda vencer. */
  if (hireDate === null || periodFor(hireDate, today).isFirstYear) return null;
  return (
    lives.find(
      (live) => live.available > 0 && live.daysLeft >= 0 && live.daysLeft <= EXPIRY_NOTICE_DAYS,
    ) ?? null
  );
};

/** Todos los saldos vigentes de un grupo de personas, para una sola lectura. */
export const liveBalanceRefsFor = (
  people: readonly { readonly id: PersonId; readonly hireDate: CalendarDate | null }[],
  today: CalendarDate,
  expiryMonths: number,
): readonly BalanceRef[] =>
  people.flatMap((person) =>
    livePeriodsFor(person.hireDate, today, expiryMonths).map((period) => ({
      personId: person.id,
      periodYear: period.year,
    })),
  );

/** Lo mismo, agrupado por persona. */
export const liveBalancesByPerson = (
  people: readonly { readonly id: PersonId; readonly hireDate: CalendarDate | null }[],
  balances: readonly Balance[],
  today: CalendarDate,
  expiryMonths: number,
): ReadonlyMap<PersonId, readonly LiveBalance[]> =>
  new Map(
    people.map((person) => [
      person.id,
      pairLiveBalances(
        livePeriodsFor(person.hireDate, today, expiryMonths),
        balances,
        person.id,
        today,
      ),
    ]),
  );
